// src/pages/components/Header.jsx
"use client";

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import strings from "../../constants/strings.json";
// Removed: import { gsap } from "gsap"; // Animation library removed

export default function Header({ locale = "en" }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const localeStrings = strings[locale] ?? strings["en"];
  const header = localeStrings.header;

  // Animation references removed

  const location = useLocation();

  const navItems = [
    { to: "/", label: header.nav_home, isHash: false },
    { to: "#about", label: header.nav_features, isHash: true },
    { to: "#services", label: header.nav_cources, isHash: true },
    { to: "#contact", label: header.nav_contact, isHash: true },
  ];

  // Handle scroll for header background change (kept)
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route/hash change (kept)
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // Close menu on Escape key (kept)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);


  // helper to handle same-page hash navigation (kept)
  const handleHashClick = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.setAttribute("tabindex", "-1");
      el.focus({ preventScroll: true });
      window.setTimeout(() => el.removeAttribute("tabindex"), 1000);
    } else {
      window.location.hash = hash;
    }
  };


  return (
      <header
          className={`fixed w-full top-0 z-50 transition-all duration-500 ease-out ${
              scrolled
                  ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-200/50 py-3"
                  : "bg-white/80 backdrop-blur-sm py-4"
          }`}
      >
        <nav className="mx-auto max-w-7xl px-6">
          <div className="flex items-center justify-between">
            {/* Logo + Brand (Animation classes and refs removed) */}
            <Link
                to="/"
                className="flex items-center gap-3 group"
                onClick={() => setOpen(false)}
            >
              <div className="relative w-12 h-12 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                <img
                    src="/assets/images/logo.jpg"
                    alt={header.logo_alt}
                    // Animation classes and refs removed
                    className="w-full h-full object-contain mix-blend-multiply"
                />
              </div>
              <span
                  // Animation classes and refs removed
                  className="brand-name text-2xl font-bold bg-gradient-to-r from-[#174143] to-[#3d9ea4] bg-clip-text text-transparent group-hover:from-[#3d9ea4] group-hover:to-[#174143] transition-all duration-500"
              >
              {header.brand_name}
            </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item, idx) => {
                // FIX: Use explicit dark color hex for immediate visibility
                const linkClasses = "nav-link relative px-4 py-2 font-semibold text-[#0f2d2f] hover:text-[#174143] transition-colors duration-300 group";

                if (item.isHash) {
                  return (
                      <a
                          key={item.to}
                          href={item.to}
                          onClick={handleHashClick(item.to)}
                          className={linkClasses}
                      >
                        <span className="relative z-10">{item.label}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#174143]/5 to-[#3d9ea4]/5 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 origin-center"></div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#174143] to-[#3d9ea4] group-hover:w-3/4 transition-all duration-300"></div>
                      </a>
                  );
                } else {
                  return (
                      <Link
                          key={item.to}
                          to={item.to}
                          className={linkClasses}
                          onClick={() => setOpen(false)}
                      >
                        <span className="relative z-10">{item.label}</span>
                        <div className="absolute inset-0 bg-gradient-to-r from-[#174143]/5 to-[#3d9ea4]/5 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 origin-center"></div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#174143] to-[#3d9ea4] group-hover:w-3/4 transition-all duration-300"></div>
                      </Link>
                  );
                }
              })}
            </div>

            {/* Mobile Menu Button */}
            <button
                type="button"
                onClick={() => setOpen((s) => !s)}
                aria-expanded={open}
                aria-label={open ? "Close menu" : "Open menu"}
                className="md:hidden relative w-10 h-10 rounded-xl bg-gray-100 hover:bg-gradient-to-r hover:from-[#174143] hover:to-[#3d9ea4] transition-all duration-300 group"
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
              <span
                  className={`w-5 h-0.5 bg-gray-700 group-hover:bg-white rounded-full transition-all duration-300 ${open ? "rotate-45 translate-y-2" : ""}`}
              ></span>
                <span
                    className={`w-5 h-0.5 bg-gray-700 group-hover:bg-white rounded-full transition-all duration-300 ${open ? "opacity-0" : ""}`}
                ></span>
                <span
                    className={`w-5 h-0.5 bg-gray-700 group-hover:bg-white rounded-full transition-all duration-300 ${open ? "-rotate-45 -translate-y-2" : ""}`}
                ></span>
              </div>
            </button>
          </div>
        </nav>

        {/* Mobile Dropdown */}
        {open && (
            <div className="md:hidden mt-2 mx-6 mb-4 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden animate-slideDown">
              <div className="p-2">
                {navItems.map((item) => {
                  if (item.isHash) {
                    return (
                        <a
                            key={item.to}
                            href={item.to}
                            onClick={handleHashClick(item.to)}
                            className="block px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-[#174143]/10 hover:to-[#3d9ea4]/10 hover:text-[#174143] font-medium transition-all duration-300"
                        >
                          {item.label}
                        </a>
                    );
                  } else {
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setOpen(false)}
                            className="block px-4 py-3 rounded-xl text-gray-700 hover:bg-gradient-to-r hover:from-[#174143]/10 hover:to-[#3d9ea4]/10 hover:text-[#174143] font-medium transition-all duration-300"
                        >
                          {item.label}
                        </Link>
                    );
                  }
                })}
              </div>
            </div>
        )}
      </header>
  );
}